import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { assetUrl } from "fusion-core";
import {
  IconEye,
  IconEyeOff,
  IconLock,
  IconMail,
  IconShield,
  IconSpinner,
} from "./icons";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { toast } from "sonner";
import {
  getRememberedCredentials,
  isStoredTokenValid,
  purgeExpiredToken,
  setRememberedCredentials,
  setStoredToken,
} from "./authStorage";

export default function Login() {
  const [credentials, setCredentials] = useState(() =>
    getRememberedCredentials()
  );
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const hasValidSession = isStoredTokenValid();

  useEffect(() => {
    purgeExpiredToken();
    if (isStoredTokenValid()) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/users/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        setRememberedCredentials(credentials.email, credentials.password);
        setStoredToken(data.token);
        toast.success("Signed in successfully");
        navigate("/admin/dashboard");
      } else {
        toast.error(data.message || "Invalid email or password");
      }
    } catch {
      toast.error("Could not sign in. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasValidSession) {
    return (
      <div className="admin-login admin-login--loading">
        <IconSpinner size={32} />
        <p>Signing you in…</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={assetUrl("./styles/Login.css")} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Helmet>

      <div className="admin-login">
        <div className="admin-login__glow admin-login__glow--one" aria-hidden="true" />
        <div className="admin-login__glow admin-login__glow--two" aria-hidden="true" />

        <div className="admin-login__shell">
          <aside className="admin-login__brand" aria-hidden="true">
            <div className="admin-login__brand-badge">
              <IconShield size={24} />
            </div>
            <p className="admin-login__eyebrow">Secure access</p>
            <h1>Admin Portal</h1>
            <p className="admin-login__brand-copy">
              Manage scheme features, categories, users, and addons from one
              place.
            </p>
          </aside>

          <main className="admin-login__panel">
            <div className="admin-login__panel-inner">
              <header className="admin-login__header">
                <p className="admin-login__eyebrow admin-login__eyebrow--dark">
                  Sign in
                </p>
                <h2>Welcome back</h2>
                <p className="admin-login__subtitle">
                  Your details are saved on this device. You&apos;ll stay signed
                  in for 30 days after you sign in.
                </p>
              </header>

              <form onSubmit={handleSubmit} className="admin-login__form" noValidate>
                <div className="admin-login__field">
                  <label htmlFor="email">Email address</label>
                  <div className="admin-login__input-wrap">
                    <IconMail size={18} className="admin-login__input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={credentials.email}
                      onChange={(e) => {
                        const email = e.target.value;
                        setCredentials((prev) => {
                          const next = { ...prev, email };
                          setRememberedCredentials(email, next.password);
                          return next;
                        });
                      }}
                      required
                      placeholder="you@company.com"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="admin-login__field">
                  <label htmlFor="password">Password</label>
                  <div className="admin-login__input-wrap">
                    <IconLock size={18} className="admin-login__input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      value={credentials.password}
                      onChange={(e) => {
                        const password = e.target.value;
                        setCredentials((prev) => {
                          const next = { ...prev, password };
                          setRememberedCredentials(next.email, password);
                          return next;
                        });
                      }}
                      required
                      placeholder="Enter your password"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="admin-login__password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      disabled={isSubmitting}
                    >
                      {showPassword ? (
                        <IconEyeOff size={18} />
                      ) : (
                        <IconEye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="admin-login__submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <IconSpinner size={18} />
                      Signing in…
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </form>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
